import React, { useState, useEffect, useCallback } from 'react';
import { Menu, X, Globe } from 'lucide-react';

const NAV_LINKS = [
  { href: '#home', label: 'Home' },
  { href: '#about', label: 'Il Casale' },
  { href: '#gallery', label: 'Galleria' },
  { href: '#attractions', label: 'Territorio' },
  { href: '#location', label: 'Location' },
  { href: '#contact', label: 'Contatti' }
];

const useScrolled = (threshold = 40) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);
  return scrolled;
};

const Logo = ({ scrolled }) => (
  <a href="#home" className="flex items-center gap-3 group">
    <div className={`w-11 h-11 rounded-full border flex items-center justify-center transition-colors ${scrolled ? 'border-[#3A4A2C]' : 'border-white/80'}`}>
      <span className={`font-serif italic text-lg leading-none ${scrolled ? 'text-[#3A4A2C]' : 'text-white'}`}>Gl</span>
    </div>
    <div className="flex flex-col leading-tight">
      <span className={`font-serif tracking-widest text-sm uppercase transition-colors ${scrolled ? 'text-[#1f1f1f]' : 'text-white'}`}>Genius Loci</span>
      <span className={`text-[10px] tracking-[0.3em] uppercase transition-colors ${scrolled ? 'text-[#8a7a5c]' : 'text-white/70'}`}>Umbria · Casale</span>
    </div>
  </a>
);

const DesktopNav = ({ scrolled }) => (
  <nav className="hidden lg:flex items-center gap-8">
    {NAV_LINKS.map(l => (
      <a
        key={l.href}
        href={l.href}
        className={`text-sm tracking-wide relative group ${scrolled ? 'text-[#2b2b2b]' : 'text-white'}`}
      >
        {l.label}
        <span className={`absolute -bottom-1 left-0 h-px w-0 group-hover:w-full transition-all duration-300 ${scrolled ? 'bg-[#3A4A2C]' : 'bg-white'}`}></span>
      </a>
    ))}
  </nav>
);

const MobileMenu = ({ open, onClose }) => {
  if (!open) return null;
  return (
    <div className="lg:hidden bg-[#faf7f2] border-t border-[#e8e0cf]">
      <div className="flex flex-col px-6 py-6 gap-5">
        {NAV_LINKS.map(l => (
          <a
            key={l.href}
            href={l.href}
            onClick={onClose}
            className="text-[#2b2b2b] text-base tracking-wide"
          >
            {l.label}
          </a>
        ))}
        <a href="#contact" onClick={onClose} className="mt-2 px-5 py-3 text-xs tracking-[0.2em] uppercase border border-[#3A4A2C] text-[#3A4A2C] text-center">Prenota</a>
      </div>
    </div>
  );
};

const NavActions = ({ scrolled, lang, onToggleLang, open, onToggleMenu }) => (
  <div className="flex items-center gap-4">
    <button
      onClick={onToggleLang}
      className={`hidden sm:flex items-center gap-1.5 text-xs tracking-widest ${scrolled ? 'text-[#3A4A2C]' : 'text-white'}`}
    >
      <Globe size={14} />
      {lang}
    </button>

    <a
      href="#contact"
      className={`hidden md:inline-flex items-center px-5 py-2.5 text-xs tracking-[0.2em] uppercase border transition-colors duration-300 ${
        scrolled
          ? 'border-[#3A4A2C] text-[#3A4A2C] hover:bg-[#3A4A2C] hover:text-[#faf7f2]'
          : 'border-white text-white hover:bg-white hover:text-[#3A4A2C]'
      }`}
    >
      Prenota
    </a>

    <button
      className={`lg:hidden ${scrolled ? 'text-[#1f1f1f]' : 'text-white'}`}
      onClick={onToggleMenu}
      aria-label="menu"
    >
      {open ? <X size={26} /> : <Menu size={26} />}
    </button>
  </div>
);

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState('IT');
  const scrolled = useScrolled(40);

  const toggleLang = useCallback(() => setLang(prev => (prev === 'IT' ? 'EN' : 'IT')), []);
  const toggleMenu = useCallback(() => setOpen(prev => !prev), []);
  const closeMenu = useCallback(() => setOpen(false), []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? 'bg-[#faf7f2]/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-10 py-5">
        <Logo scrolled={scrolled} />
        <DesktopNav scrolled={scrolled} />
        <NavActions
          scrolled={scrolled}
          lang={lang}
          onToggleLang={toggleLang}
          open={open}
          onToggleMenu={toggleMenu}
        />
      </div>
      <MobileMenu open={open} onClose={closeMenu} />
    </header>
  );
};

export default Navbar;
